import * as React from 'react';
import type { FormEvent } from 'react';
import {
  ApiError,
  getGetMyPresencePlanQueryKey,
  getListMyLocationNeedsQueryKey,
  getListMyTimedPlansQueryKey,
  getListBookingsQueryKey,
  getSearchAvailabilityQueryKey,
  usePostAiAssistantChat,
  usePostAiAssistantConfirm,
  type AssistantProposedAction,
  type TimedPlan,
  type AssistantScreenHint,
  type AssistantSelectableOption,
  type AssistantInputField
} from '@cdf/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateTwinStatusQueries } from '@/lib/twin-query-invalidation';
import { invalidateInventoryLifecycle } from '@/lib/inventory-lifecycle-invalidation';
import { parseTextSelectableOptions } from '@/lib/assistant-options';

export interface AssistantChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposedActions?: AssistantProposedAction[];
  selectableOptions?: AssistantSelectableOption[];
  screenHints?: AssistantScreenHint[];
  requestedFields?: AssistantInputField[];
}

function describeTimedPlanSave(plan: TimedPlan | undefined): string {
  if (!plan) return 'Timed plan saved. Open My Work Plan to review booking adjustments.';
  const outcomes = plan.adjustments.filter((item) => item.planRevision === plan.revision);
  if (!outcomes.length) return `Timed plan saved with ${plan.stops.length} stop(s). No linked reservations needed adjustment.`;
  const details = outcomes.slice(0, 4).map((item) => {
    if (item.state === 'succeeded') {
      if (item.action === 'cancel') return `Removed-stop booking ${item.originalBookingId} cancelled.`;
      return item.replacementBookingId
        ? `Replacement ${item.replacementBookingId} secured; original released.`
        : `Original booking ${item.originalBookingId} still meets the stop.`;
    }
    if (item.state === 'awaiting_calendar') {
      return `Room replacement awaiting acceptance; original ${item.originalBookingId} remains reserved.`;
    }
    if (item.state === 'unresolved') {
      return `Needs attention: ${item.reason ?? 'no suitable replacement'}. Original ${item.originalBookingId} remains reserved.`;
    }
    return `Adjustment pending; original ${item.originalBookingId} remains reserved.`;
  });
  return `Timed plan saved (revision ${plan.revision}). ${details.join(' ')}${outcomes.length > 4 ? ' Open My Work Plan for all outcomes.' : ''}`;
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface AssistantChatContextValue {
  messages: AssistantChatMessage[];
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  conversationId: string | undefined;
  error: string | null;
  actingToken: string | null;
  dismissedTokens: Set<string>;
  isChatPending: boolean;
  isConfirmPending: boolean;
  sendMessage: (e?: FormEvent) => Promise<void>;
  /** Send a free-form user message (e.g. from a selectable option chip). */
  sendText: (text: string) => Promise<void>;
  onConfirm: (action: AssistantProposedAction) => Promise<void>;
  onDecline: (action: AssistantProposedAction) => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  openDrawer: () => void;
}

const AssistantChatContext = React.createContext<AssistantChatContextValue | null>(null);

export function AssistantChatProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = React.useState<AssistantChatMessage[]>([]);
  const [input, setInput] = React.useState('');
  const [conversationId, setConversationId] = React.useState<string | undefined>();
  const [error, setError] = React.useState<string | null>(null);
  const [actingToken, setActingToken] = React.useState<string | null>(null);
  const [dismissedTokens, setDismissedTokens] = React.useState<Set<string>>(
    () => new Set()
  );
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const chat = usePostAiAssistantChat();
  const confirm = usePostAiAssistantConfirm();

  const sendText = React.useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || chat.isPending) return;
      setError(null);
      setInput('');
      const userMsg: AssistantChatMessage = { id: uid(), role: 'user', content: text };
      setMessages((prev) => [...prev, userMsg]);

      try {
        const result = await chat.mutateAsync({
          data: { message: text, conversationId }
        });
        setConversationId(result.conversationId);
        const structured = result.selectableOptions ?? [];
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: result.reply,
            proposedActions: result.proposedActions,
            selectableOptions:
              structured.length > 0
                ? structured
                : parseTextSelectableOptions(result.reply),
            screenHints: result.screenHints,
            requestedFields: result.requestedFields
          }
        ]);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Assistant request failed.';
        setError(message);
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: 'assistant',
            content: 'Sorry — I could not complete that request. Please try again.'
          }
        ]);
      }
    },
    [chat, conversationId]
  );

  const sendMessage = React.useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      const text = input.trim();
      if (!text) return;
      await sendText(text);
    },
    [input, sendText]
  );

  const onConfirm = React.useCallback(
    async (action: AssistantProposedAction) => {
      setActingToken(action.confirmationToken);
      setError(null);
      try {
        const result = await confirm.mutateAsync({
          data: { confirmationToken: action.confirmationToken }
        });
        setDismissedTokens((prev) => new Set(prev).add(action.confirmationToken));
        if (action.lifecycleReview) {
          await invalidateInventoryLifecycle(queryClient);
        } else if (result.action === 'admin_action') {
          // Administration changes can touch features, inventory, policies,
          // access and reporting — refresh every active query.
          await queryClient.invalidateQueries();
        } else {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: getListBookingsQueryKey() }),
            queryClient.invalidateQueries({ queryKey: getSearchAvailabilityQueryKey() }),
            queryClient.invalidateQueries({ queryKey: getGetMyPresencePlanQueryKey() }),
            queryClient.invalidateQueries({ queryKey: getListMyTimedPlansQueryKey() }),
            queryClient.invalidateQueries({ queryKey: getListMyLocationNeedsQueryKey() }),
            queryClient.invalidateQueries({ predicate: (query) =>
              typeof query.queryKey[0] === 'string' &&
              query.queryKey[0].startsWith('/api/me/timed-plans/') }),
            invalidateTwinStatusQueries(queryClient)
          ]);
        }
        const okLine =
          result.action === 'create_booking'
            ? `Confirmed. Booking ${result.booking?.id ?? ''} created (source: ai).`
            : result.action === 'create_booking_series'
              ? `Confirmed. Recurring series created: ${result.series?.bookings?.length ?? 0} booked, ${result.series?.skipped?.length ?? 0} skipped.`
              : result.action === 'set_work_plan'
                ? `Confirmed. Updated your work plan (${result.workPlanDays?.length ?? 0} day(s)).`
                : result.action === 'set_timed_plan'
                  ? describeTimedPlanSave(result.timedPlan)
                  : result.action === 'decide_location_needs'
                    ? `Confirmed. Reviewed ${result.locationNeeds?.length ?? 0} location need(s).`
                    : result.action === 'admin_action'
                  ? `Confirmed. ${result.adminResult?.summary ?? 'Administration change applied.'}`
                  : `Confirmed. Booking ${result.cancelledId ?? result.booking?.id ?? ''} cancelled.`;
        setMessages((prev) => [
          ...prev,
          { id: uid(), role: 'assistant', content: okLine.trim() }
        ]);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Confirmation failed.';
        if (action.lifecycleReview && err instanceof ApiError && (err.status === 409 || err.status === 410)) {
          setDismissedTokens(prev => new Set(prev).add(action.confirmationToken));
          setError('The reviewed impact changed. A refreshed proposal must be reviewed and confirmed again.');
          await sendText('Please review this inventory lifecycle change again with current child and booking impact: ' + JSON.stringify(action.payload));
        } else setError(message);
      } finally {
        setActingToken(null);
      }
    },
    [confirm, queryClient, sendText]
  );

  const onDecline = React.useCallback((action: AssistantProposedAction) => {
    setDismissedTokens((prev) => new Set(prev).add(action.confirmationToken));
    setMessages((prev) => [
      ...prev,
      {
        id: uid(),
        role: 'assistant',
        content:
          action.type === 'set_work_plan' || action.type === 'set_timed_plan'
            ? 'Okay — I cancelled that work-plan proposal. Nothing was saved or rebooked.'
            : action.type === 'decide_location_needs'
              ? 'Okay — those location needs were not changed.'
              : action.type === 'admin_action'
              ? 'Okay — I cancelled that administration change. Nothing was saved.'
              : 'Okay — I cancelled that proposal. Nothing was booked.'
      }
    ]);
  }, []);

  const openDrawer = React.useCallback(() => setDrawerOpen(true), []);

  const value = React.useMemo<AssistantChatContextValue>(
    () => ({
      messages,
      input,
      setInput,
      conversationId,
      error,
      actingToken,
      dismissedTokens,
      isChatPending: chat.isPending,
      isConfirmPending: confirm.isPending,
      sendMessage,
      sendText,
      onConfirm,
      onDecline,
      drawerOpen,
      setDrawerOpen,
      openDrawer
    }),
    [
      messages,
      input,
      conversationId,
      error,
      actingToken,
      dismissedTokens,
      chat.isPending,
      confirm.isPending,
      sendMessage,
      sendText,
      onConfirm,
      onDecline,
      drawerOpen,
      setDrawerOpen,
      openDrawer
    ]
  );

  return (
    <AssistantChatContext.Provider value={value}>{children}</AssistantChatContext.Provider>
  );
}

export function useAssistantChat() {
  const ctx = React.useContext(AssistantChatContext);
  if (!ctx) {
    throw new Error('useAssistantChat must be used within AssistantChatProvider');
  }
  return ctx;
}

/** Soft lookup for header controls during HMR remount races. */
export function useAssistantChatOptional() {
  return React.useContext(AssistantChatContext);
}
